import ChatSurfaceSwitcher from 'tripPlanner/components/Chat/ChatSurfaceSwitcher'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ModalContext from 'contexts/ModalContext'
import ChatPanel from 'tripPlanner/assistant/components/AssistantChatPanel'
import AssistantMessageTimeline from 'tripPlanner/assistant/components/AssistantMessageTimeline'
import MessagesList from 'tripPlanner/components/Chat/MessagesList'
import ChatMessageInput from 'tripPlanner/components/Chat/ChatMessageInput'
import { isTripPlannerRegenerationCompatible } from 'tripPlanner/assistant/surfacePolicy'
import AddBookingModal from 'tripPlanner/components/Chat/AddBookingModal'
import ChatInputContextBar from '../components/ChatInputContextBar'
import AiContextTrace, { type TracePoint } from '../components/AiContextTrace'
import SalesAgentSummaryPanel from '../components/SalesAgentSummaryPanel'
import ChatFeedbackPopup from '../components/ChatFeedbackPopup'
import { ChatMessageActionsProvider, type OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import type { ChatMapOnMapData } from 'tripPlanner/assistant/mapTypes'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import { useChatFiles } from '../contexts/ChatFilesContext'
import { InputWrapper } from '../styles/layoutStyles'
import type { HotelMapViewData, ExperienceMapViewData, MessageFeedbackReaction } from 'tripPlanner/api/types'
import { useClipboard } from 'tripPlanner/assistant/hooks/useClipboard'
import { useFeedback } from 'tripPlanner/assistant/hooks/useFeedback'
import { getChatErrorMessage, getChatErrorHeading } from '../utils/chatErrorMessages'
import { useTripPlannerEventBus, useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useStaffRewind } from 'tripPlanner/assistant/contexts/StaffRewindContext'
import {
  buildAskAIContextForDay,
  buildAskAIContextForItem,
  buildAskAIContextForListRow,
} from '../utils/buildAskAIContext'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useChatScrollContext } from '../contexts/ChatScrollContext'
import { useChatStreamMessages } from '../contexts/ChatStreamMessagesContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { useMapInteraction } from '../contexts/MapInteractionContext'
import { useChatFeedbackPopup } from '../hooks/useChatFeedbackPopup'
import { buildPlaceDrawerOpenedActivityEntry } from './buildPlaceDrawerOpenedActivityEntry'
import { buildLocationSuggestionOpenedActivityEntry } from './buildLocationSuggestionOpenedActivityEntry'
import { buildMessageFeedbackActivityEntry } from './buildMessageFeedbackActivityEntry'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import BodyText from 'LuxKit/Typography/BodyText'
import { appendContextTag } from 'tripPlanner/utils/contextTag'
import { shouldDefaultSalesSummaryCollapsed } from '../utils/salesAgentSummary'
import noop from 'lib/function/noop'
import { useScheduleCallbackChatAction } from 'tripPlanner/components/Chat/hooks/useScheduleCallbackChatAction'
import FlightSearchDrawer from '../components/FlightSearchDrawer/FlightSearchDrawer'
import ChatCart from '../components/ChatCart'
import {
  useDrawerAskAIComposerContext,
  type ChatInputContext,
} from '../hooks/useDrawerAskAIComposerContext'
import { ChatMapLiveSlotProvider } from 'tripPlanner/components/Chat/ChatMapLiveSlotContext'
import GuestChatLoginPrompt from '../components/GuestChatLoginPrompt'

interface Props {
  chatId: number | undefined
  isLoggedIn: boolean
  isDevMode: boolean
  isSpoofed: boolean
  hiddenFromCustomer?: boolean
  spoofingAgentEmail?: string
  navigateToNewChatRoute: () => void
  handleSubmit: (message: string) => Promise<void>
  handleMultiChoiceSelectionChange: (selection: Array<string>) => void
  activeMultiChoiceSelection: Array<string>
  prefilledSelectionTags?: Array<string>
  mobileSelectionText: string | undefined
  offerContextTag: { label: string; offerId: string } | undefined
  handleRemoveTag: (tag: string) => void
  onRemovePrefilledSelectionTag?: (tag: string) => void
  selectedBookingItemIds?: Array<string>
  onBookingItemsAdded?: (bookingItemIds: Array<string>) => void
  chatInputRef: React.RefObject<HTMLTextAreaElement | null>
  autoFocusChatInput?: boolean
  /**
   * Set by surfaces whose Timeline/List tab carries its own Ask AI composer, so the
   * hidden chat tab doesn't also swallow the highlight. The surface that owns the tabs
   * is the only thing that knows this — deriving it here would guess.
   */
  deferAskAIHighlightToLocalComposer?: boolean
  dismissKeyboardOnSubmit?: boolean
  compactDisclaimer?: boolean
  enableMapActions?: boolean
  fitAvailableHeight?: boolean
  showMessageTimeline?: boolean
  showAddedItineraryItemLink?: boolean
}

const DEFAULT_PREFILLED_SELECTION_TAGS: Array<string> = []
const DEFAULT_SELECTED_BOOKING_ITEM_IDS: Array<string> = []

function ChatContainer({
  chatId,
  isLoggedIn,
  isDevMode,
  isSpoofed,
  hiddenFromCustomer = false,
  spoofingAgentEmail,
  navigateToNewChatRoute,
  handleSubmit,
  handleMultiChoiceSelectionChange,
  activeMultiChoiceSelection,
  prefilledSelectionTags = DEFAULT_PREFILLED_SELECTION_TAGS,
  mobileSelectionText,
  offerContextTag,
  handleRemoveTag,
  onRemovePrefilledSelectionTag = noop,
  selectedBookingItemIds = DEFAULT_SELECTED_BOOKING_ITEM_IDS,
  onBookingItemsAdded,
  chatInputRef,
  autoFocusChatInput = true,
  deferAskAIHighlightToLocalComposer = false,
  dismissKeyboardOnSubmit = false,
  compactDisclaimer = false,
  enableMapActions = true,
  fitAvailableHeight = false,
  showMessageTimeline = false,
  showAddedItineraryItemLink = false,
}: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const { canStaffRewind } = useStaffRewind()
  const showModal = useContext(ModalContext)
  const { onCardHover } = useMapInteraction()
  const { files, setFiles } = useChatFiles()

  const { regenerateResponse: streamingRegenerate, sendPostAuthMessage, setFollowTail, submitMessage } = useChatActions()
  const { isStreaming, isStreamOpen, loadingState, isStalled, isOnline, reasoningSummaries, toolCallInfo } = useChatStreamStatus()
  const { handleScroll: handleMessagesScroll, isAtBottom, hasContentOutOfView, enableFollowAndScrollToBottom, dynamicSpacerHeight, messagesWrapperRef } = useChatScrollContext()

  const {
    messages,
    filteredMessages,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
  } = useChatStreamMessages()

  const { itineraryData, isFlightCartMutationPending } = useItineraryContext()

  // ── Local state ──

  const [messageInputValue, setMessageInputValue] = useState('')
  const [chatInputContext, setChatInputContext] = useState<ChatInputContext | undefined>(undefined)
  const [contextTrace, setContextTrace] = useState<{ origin: TracePoint; key: number } | undefined>(undefined)
  const contextBarRef = useRef<HTMLDivElement>(null)
  const traceKeyRef = useRef(0)
  const historyOverlayTouchStartYRef = useRef<number | undefined>(undefined)

  // ── Derived values ──

  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'
  const isListLoading = loadingState.isListLoading
  const isUpdatingItinerary = loadingState.isUpdatingItinerary
  const activeToolLoadingKey = loadingState.activeToolLoadingKey
  const shouldShowSalesSummary = isSpoofed && chatId !== undefined
  const shouldStartSalesSummaryCollapsed = shouldDefaultSalesSummaryCollapsed(spoofingAgentEmail)

  const messagesWithoutLocationEntities = useMemo(() => {
    return filteredMessages.map(msg => ({
      ...msg,
      parts: msg.parts.filter(part => {
        if (part.type !== 'ui_component') return true
        const data = part.data
        if (data && typeof data === 'object' && 'type' in data) {
          // location_entities drive the right-panel map only — not chat UI.
          // In-chat `map` cards stay in the transcript.
          return data.type !== 'location_entities'
        }
        return true
      }),
    }))
  }, [filteredMessages])

  const lastAssistantMessage = useMemo(() => {
    return filteredMessages.findLast(m => m.role === 'assistant')
  }, [filteredMessages])

  const hasUserMessage = useMemo(() => {
    return filteredMessages.some(m => m.role === 'user')
  }, [filteredMessages])

  const lastMessageIsAssistant = filteredMessages.length > 0 && filteredMessages[filteredMessages.length - 1].role === 'assistant'
  const shouldShowAssistantActions = !!lastAssistantMessage && lastMessageIsAssistant && !isLoading && !isStreaming
  const isRegenerationCompatible = isTripPlannerRegenerationCompatible(lastAssistantMessage?.assistantTurnPolicySnapshot)

  const hasMultiChoiceSelection = activeMultiChoiceSelection.length > 0
  const hasPrefilledSelection = prefilledSelectionTags.length > 0
  const hasMobileSelection = mobileSelectionText !== undefined
  const hasFiles = files.length > 0
  const hasMessageContent = messageInputValue.trim() !== '' || hasMultiChoiceSelection || hasPrefilledSelection || hasMobileSelection || hasFiles
  const showStopButton = isStreaming || isStreamOpen
  const disableSubmit = isLoading || showStopButton || isFlightCartMutationPending || !hasMessageContent

  const allSelectedTags = useMemo(() => {
    const tags = [...prefilledSelectionTags, ...activeMultiChoiceSelection]
    if (mobileSelectionText) {
      tags.push(mobileSelectionText)
    }
    if (offerContextTag) {
      tags.push(offerContextTag.label)
    }
    return [...new Set(tags)]
  }, [activeMultiChoiceSelection, mobileSelectionText, offerContextTag, prefilledSelectionTags])

  const handleRemoveSelectedTag = useCallback((tag: string) => {
    if (prefilledSelectionTags.includes(tag)) {
      onRemovePrefilledSelectionTag(tag)
      if (activeMultiChoiceSelection.includes(tag)) {
        handleRemoveTag(tag)
      }
      return
    }

    handleRemoveTag(tag)
  }, [activeMultiChoiceSelection, handleRemoveTag, onRemovePrefilledSelectionTag, prefilledSelectionTags])

  // ── Chat error effect ──

  useEffect(() => {
    if (isChatError && chatId !== undefined) {
      const errorStatus = chatError && typeof chatError === 'object' && 'status' in chatError && typeof chatError.status === 'number'
        ? chatError.status
        : undefined
      showSnackbar(
        getChatErrorMessage(intl, errorStatus),
        'critical',
        { heading: getChatErrorHeading(intl) },
      )
      navigateToNewChatRoute()
    }
  }, [isChatError, chatId, chatError, navigateToNewChatRoute, intl])

  // ── Hooks ──

  const { copiedRecently, handleCopyResponse } = useClipboard({ lastAssistantMessage, isDevMode })

  const handleFeedbackSubmitted = useCallback((messageId: string, reaction: MessageFeedbackReaction) => {
    setUserFeedbackMap(prev => ({
      ...prev,
      [messageId]: reaction,
    }))
    // messageId is always lastAssistantMessage.id here (useFeedback only ever submits with
    // lastAssistantMessageId), so the message is always found -- the guard just keeps the return
    // type honest without asserting. Looked up from `messages`, not `filteredMessages`, and reads
    // the server-assigned messageSeq directly rather than recomputing a position by counting --
    // filteredMessages strips messages by messageType in production but not in dev, so a
    // recomputed position could drift from whatever numbering the model's own transcript implies
    // (PR review finding on #37336); messageSeq can't drift, since it's not derived from this list
    // at all.
    const targetMessage = messages.find(message => message.id === messageId)
    // Unlike a state-mutating action (e.g. default_transport_switched's !isInspecting guard),
    // feedback is a proxy for genuine customer sentiment, not an objective change that's equally
    // "real" regardless of who clicked it -- so this suppresses on any staff-driven view of this
    // chat, not just the narrower isInspecting sub-case. isSpoofed (staff impersonating the whole
    // account) and canStaffRewind (the separate staff chat-viewing tool, independent of spoofing)
    // are both reachable without the other being true.
    if (targetMessage?.messageSeq !== undefined && !isSpoofed && !canStaffRewind) {
      eventBus.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildMessageFeedbackActivityEntry(reaction, targetMessage.messageSeq)],
      })
    }
  }, [setUserFeedbackMap, messages, eventBus, isSpoofed, canStaffRewind])

  const { feedbackSubmitted, handleThumbsUp, handleThumbsDown } = useFeedback({
    chatId,
    lastAssistantMessageId: lastAssistantMessage?.id,
    existingFeedback: userFeedbackMap,
    onFeedbackSubmitted: handleFeedbackSubmitted,
  })
  const feedbackPopup = useChatFeedbackPopup({ chatId, messages })

  // ── Callbacks ──

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus()
  }, [chatInputRef])

  const {
    clearChatInputContextAfterSubmit,
    clearChatInputContextManually,
  } = useDrawerAskAIComposerContext({
    chatInputContext,
    messageInputValue,
    setChatInputContext,
    setMessageInputValue,
    focusChatInput,
  })

  // Landing point for the Ask AI context comet: the left edge of the context chip.
  const getContextTraceTarget = useCallback((): TracePoint | null => {
    const rect = contextBarRef.current?.getBoundingClientRect()
    if (!rect || rect.height === 0) return null
    return { x: rect.left + 32, y: rect.top + rect.height * 0.42 }
  }, [])

  const handleContextTraceDone = useCallback(() => {
    setContextTrace(undefined)
  }, [])

  const handleClearChatInputContext = useCallback(() => {
    clearChatInputContextManually()
  }, [clearChatInputContextManually])

  const handleRegenerateResponse = useCallback(async() => {
    if (isLoading) return
    if (!lastAssistantMessage) return
    if (!isRegenerationCompatible) return
    const lastUserMessage = messages.findLast((message) => message.role === 'user')
    if (!lastUserMessage) return
    setFollowTail(false)
    return streamingRegenerate()
  }, [isLoading, isRegenerationCompatible, lastAssistantMessage, messages, streamingRegenerate, setFollowTail])

  const handleScheduleCallback = useScheduleCallbackChatAction({
    submitMessage,
  })

  const handleSubmitWithChatInputContext = useCallback((message: string) => {
    if (chatInputContext?.message) {
      const messageWithContext = appendContextTag(
        message,
        chatInputContext.message,
        chatInputContext.label,
      )

      clearChatInputContextAfterSubmit(chatInputContext)

      return handleSubmit(messageWithContext)
    }
    return handleSubmit(message)
  }, [chatInputContext, clearChatInputContextAfterSubmit, handleSubmit])

  const handleAddBookingClick = useCallback(() => {
    showModal(
      <AddBookingModal
        onSendMessage={handleSubmitWithChatInputContext}
        disabledBookingItemIds={selectedBookingItemIds}
        onBookingItemsAdded={onBookingItemsAdded}
        isSubmitLocked={isStreaming}
        chatId={chatId}
      />,
    )
  }, [chatId, handleSubmitWithChatInputContext, isStreaming, onBookingItemsAdded, selectedBookingItemIds, showModal])

  // ── History overlay scroll forwarding ──

  const handleHistoryOverlayWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (!messagesWrapperRef.current) {
      return
    }
    event.preventDefault()
    messagesWrapperRef.current.scrollTop += event.deltaY
  }, [messagesWrapperRef])

  const handleHistoryOverlayTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const firstTouch = event.touches[0]
    if (!firstTouch) {
      return
    }
    historyOverlayTouchStartYRef.current = firstTouch.clientY
  }, [])

  const handleHistoryOverlayTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (!messagesWrapperRef.current || historyOverlayTouchStartYRef.current === undefined) {
      return
    }

    const touch = event.touches[0]
    if (!touch) {
      return
    }

    const deltaY = historyOverlayTouchStartYRef.current - touch.clientY
    if (deltaY !== 0) {
      messagesWrapperRef.current.scrollTop += deltaY
      historyOverlayTouchStartYRef.current = touch.clientY
    }
    event.preventDefault()
  }, [messagesWrapperRef])

  const handleHistoryOverlayTouchEnd = useCallback(() => {
    historyOverlayTouchStartYRef.current = undefined
  }, [])

  // ── ChatMessageActionsProvider callbacks ──

  const handleHotelMapViewClick = useCallback((data: HotelMapViewData) => {
    eventBus.dispatch({ type: 'show_hotel_on_map', data })
  }, [eventBus])

  const handleExperienceMapViewClick = useCallback((data: ExperienceMapViewData) => {
    eventBus.dispatch({ type: 'show_experience_on_map', data })
  }, [eventBus])

  const handleOfferClick = useCallback((data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => {
    eventBus.dispatch({ type: 'open_offer_drawer', ...data })
  }, [eventBus])

  const handlePlaceClick = useCallback((data: { googlePlaceId: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_drawer', ...data })
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildPlaceDrawerOpenedActivityEntry(data.placeName, 'trip_planner.chat.place_link', data.googlePlaceId)],
    })
  }, [eventBus])

  const handlePlaceSearchClick = useCallback((data: { searchQuery: string; placeName: string }) => {
    eventBus.dispatch({ type: 'open_place_search_drawer', ...data })
  }, [eventBus])

  const handleChatMapEnlarge = useCallback((data: ChatMapOnMapData) => {
    eventBus.dispatch({ type: 'show_chat_map_on_map', data })
  }, [eventBus])

  const handleLocationInfoClick = useCallback((data: LocationInfoData) => {
    eventBus.dispatch({ type: 'open_location_drawer', data })
    eventBus.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildLocationSuggestionOpenedActivityEntry(data.title)],
    })
  }, [eventBus])

  // ── Event bus subscriptions ──

  useEventSubscription('ask_ai_for_day', (event) => {
    eventBus.dispatch({ type: 'focus_floating_ask_ai' })
    if (deferAskAIHighlightToLocalComposer) return

    setChatInputContext(buildAskAIContextForDay(intl, event))
    focusChatInput()
  })

  useEventSubscription('ask_ai_for_item', (event) => {
    eventBus.dispatch({ type: 'focus_floating_ask_ai' })
    if (deferAskAIHighlightToLocalComposer) return

    setChatInputContext(buildAskAIContextForItem(intl, event))
    focusChatInput()
  })

  useEventSubscription('ask_ai_for_list_row', (event) => {
    eventBus.dispatch({ type: 'focus_floating_ask_ai' })
    if (deferAskAIHighlightToLocalComposer) return

    setChatInputContext(buildAskAIContextForListRow(intl, event))
    focusChatInput()
    if (event.origin) {
      traceKeyRef.current += 1
      setContextTrace({ origin: event.origin, key: traceKeyRef.current })
    }
  })

  useEventSubscription('reset_chat_state', () => {
    setMessageInputValue('')
    setChatInputContext(undefined)
  })

  // ── i18n ──

  const chatInputPlaceholder = chatInputContext?.placeholder
  const messagesErrorText = intl.formatMessage({
    id: 'zNUAhjKCUvuU-8zm4DQm',
    defaultMessage: 'Something went wrong displaying messages.',
    description: 'Fallback error message shown if trip planner chat messages fail to render',
  })

  // ── Render ──

  const inputContent = (
    <InputWrapper
      className={clsx({
        'has-context-bar': !!chatInputContext,
        'flush-bottom': fitAvailableHeight,
      })}
    >
      {feedbackPopup.isVisible && (
        <ChatFeedbackPopup
          onClose={feedbackPopup.dismiss}
          onSubmit={feedbackPopup.submit}
        />
      )}
      <div ref={contextBarRef}>
        {chatInputContext && (
          <ChatInputContextBar
            label={chatInputContext.label}
            onClose={handleClearChatInputContext}
          />
        )}
      </div>
      {contextTrace && (
        <AiContextTrace
          key={contextTrace.key}
          origin={contextTrace.origin}
          getTarget={getContextTraceTarget}
          onDone={handleContextTraceDone}
        />
      )}
      <ChatMessageInput
        onSubmit={handleSubmitWithChatInputContext}
        value={messageInputValue}
        onChange={setMessageInputValue}
        disableSubmit={disableSubmit}
        isStreaming={isStreaming}
        showStopButton={showStopButton}
        files={files}
        onFilesChange={setFiles}
        selectedTags={allSelectedTags}
        onAddBookingClick={handleAddBookingClick}
        showAddBookingAction={isLoggedIn}
        onRemoveTag={handleRemoveSelectedTag}
        inputRef={chatInputRef}
        placeholder={chatInputPlaceholder}
        autoFocus={autoFocusChatInput}
        dismissKeyboardOnSubmit={dismissKeyboardOnSubmit}
        compactDisclaimer={compactDisclaimer}
        chatId={chatId}
      />
    </InputWrapper>
  )

  return (
    <ChatMapLiveSlotProvider>
      <ChatMessageActionsProvider
        onMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
        onOfferClick={handleOfferClick}
        onOfferHover={onCardHover}
        onHotelMapViewClick={enableMapActions ? handleHotelMapViewClick : noop}
        onExperienceMapViewClick={enableMapActions ? handleExperienceMapViewClick : noop}
        onPlaceClick={handlePlaceClick}
        onPlaceSearchClick={handlePlaceSearchClick}
        onChatMapEnlarge={handleChatMapEnlarge}
        onLocationInfoClick={handleLocationInfoClick}
        onScheduleCallback={handleScheduleCallback}
        onSendMessage={handleSubmit}
        onAuthenticatedFollowUp={sendPostAuthMessage}
        itineraryData={itineraryData}
        isDevMode={isDevMode}
      >
        <ChatPanel
          messagesWrapperRef={messagesWrapperRef}
          onScroll={handleMessagesScroll}
          isOverlay={false}
          showHistoryOverlay={false}
          onHistoryWheel={handleHistoryOverlayWheel}
          onHistoryTouchStart={handleHistoryOverlayTouchStart}
          onHistoryTouchMove={handleHistoryOverlayTouchMove}
          onHistoryTouchEnd={handleHistoryOverlayTouchEnd}
          inputSlot={inputContent}
          isAtBottom={isAtBottom}
          hasContentOutOfView={hasContentOutOfView}
          onScrollToBottomButtonClick={enableFollowAndScrollToBottom}
          dynamicSpacerHeight={dynamicSpacerHeight}
          fitAvailableHeight={fitAvailableHeight}
          timelineSlot={showMessageTimeline ? (
            <AssistantMessageTimeline
              messages={messagesWithoutLocationEntities}
              messagesWrapperRef={messagesWrapperRef}
            />
          ) : undefined}
          topSlot={<>
            <ChatSurfaceSwitcher chatId={chatId} disabled={isStreaming} />
            <SalesAgentSummaryPanel
              chatId={chatId}
              enabled={shouldShowSalesSummary}
              initiallyCollapsed={shouldStartSalesSummaryCollapsed}
            />
          </>}
        >
          <ErrorBoundary fallback={() => <BodyText variant="medium" colour="critical">{messagesErrorText}</BodyText>}>
            <MessagesList
              messages={messagesWithoutLocationEntities}
              chatId={chatId}
              isLoading={isListLoading}
              isUpdatingItinerary={isUpdatingItinerary}
              activeToolLoadingKey={activeToolLoadingKey}
              isStalled={isStalled}
              isOnline={isOnline}
              reasoningSummaries={reasoningSummaries}
              toolCallInfo={toolCallInfo}
              loadingStatus={loadingState.status}
              showAssistantActions={shouldShowAssistantActions}
              isPrivateChat={isSpoofed && hiddenFromCustomer}
              showRegenerateButton={isRegenerationCompatible}
              isStreaming={isStreaming}
              onRegenerate={handleRegenerateResponse}
              onCopy={handleCopyResponse}
              disableRegenerateButton={isLoading || !hasUserMessage || !isRegenerationCompatible}
              disableCopyButton={!lastAssistantMessage || isLoading}
              copiedRecently={copiedRecently}
              onThumbsUp={handleThumbsUp}
              onThumbsDown={handleThumbsDown}
              feedbackSubmitted={feedbackSubmitted}
              externalSelection={activeMultiChoiceSelection}
              FlightSearchDrawerComponent={FlightSearchDrawer}
              CartComponent={ChatCart}
              showAddedItineraryItemLink={showAddedItineraryItemLink}
              thirdAssistantResponseSlot={!isLoggedIn ? <GuestChatLoginPrompt /> : undefined}
            />
          </ErrorBoundary>
        </ChatPanel>
      </ChatMessageActionsProvider>
    </ChatMapLiveSlotProvider>
  )
}

export default ChatContainer
